<template>
  <div class="px-10 ml-13">
    <card title="Filtros">
      <div class="batch-filter-controls mt-n2">
        <SearchFieldSelector
          :default-start-date="dateSelected && dateSelected.start"
          :default-end-date="dateSelected && dateSelected.end"
          :text-value="dateQuery"
          class="batch-filter-period"
          :date-range-value="dateSelected"
          :clean-date="cleanDate"
          :options="dateSelectOptions"
          :default-option-index="defaultOptionIndex"
          @updateSearchValue="setDate"
          @enterPressed="submitSearch"
        />

        <FieldContainer
          v-if="isAllowedCollectionPoint"
          class="batch-filter-collection-point"
          :title="collectionPoint && collectionPoint.value"
          label="Posto de coleta"
        >
          <SelectSearch
            id="txtCollectionPointName"
            ref="refSearchCollectionPoint"
            :value="collectionPoint"
            :options="collectionPointOptions"
            placeholder="Selecione um posto de coleta"
            :uppercase-all-items="true"
            :footer-enabled="false"
            :no-results-message-enabled="true"
            :icon-visible="false"
            no-results-text="Não foi encontrado posto de coleta cadastrado com esse nome"
            @change="setCollectionPoint"
            @keyup.enter.native="submitSearch"
          />
        </FieldContainer>

        <md-button
          class="btn btn-primary batch-filter-submit"
          @click="submitSearch"
        >
          <svg-loader :src="'/static/images/icon-generate-list.svg'" />
          Listar
        </md-button>
      </div>
      <div class="align-center">
        <md-layout class="flex-row mt-4 gap-4">
          <button-icon
            v-for="iconItem in icons"
            :key="iconItem.label"
            :color="iconItem.color"
            :label="iconItem.label"
            :src-icon="iconItem.src"
            :value="iconItem.value"
            :hint="hintPackagingType(iconItem.value)"
            @input="(event) => setPackagingType(iconItem, event)"
          />
        </md-layout>
      </div>
    </card>

    <CollectionPointModal
      title="Defina um posto de coleta"
      hide-button
      :show-modal="showModalCollectionPointModal"
      @change="setCollectionPointModal"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import Cookies from "js-cookie";
import notify from "@/api/notification";

import { formatDate } from "@/utils/date-utils";
import { restorePrintFilters, persistPrintFilters, arePrintFiltersEqual } from "@/utils/print-filter-session";
import { logCollectionPointError } from "@/utils/collection-point-error";
import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
} from "@/enums";

import Card from "@/components/common/Card.vue";
import ButtonIcon from "@/components/common/ButtonIcon.vue";
import SearchFieldSelector from "@/components/common/SearchFieldSelectorV2.vue";
import FieldContainer from "@/components/common/FieldContainer.vue";
import SelectSearch from "@/components/common/SelectSearch.vue";
import CollectionPointModal from "../CollectionPointModal.vue";

function collectionPointName(point) {
  if (!point || typeof point !== 'object' || Array.isArray(point)) return undefined;
  return [point.name, point.value, point.label].find(value => typeof value === 'string' && value);
}

export default {
  name: "BatchesFilter",

  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    FieldContainer,
    SelectSearch,
    CollectionPointModal,
  },

  data() {
    return {
      refSearchCollectionPoint: null,

      today: new Date(),
      dateSelected: null,
      typeDateSelected: null,
      dateQuery: "",
      cleanDate: 0,
      defaultOptionIndex: 0,

      packagingSelected: [],
      collectionPoint: null,
      integratedRoot: "",

      showModalCollectionPointModal: false,

      limitDaysBatches: 10,
      filtersInitialized: false,
      pendingSearch: null,
      lastAppliedFilters: null,
      hasRestoredCollectionPoint: false,

      packagingFrozenId: BATCH_PACKAGING_FROZEN_ID.toString(),
      packagingRefrigeratedId: BATCH_PACKAGING_REFRIGERATED_ID.toString(),
      packagingTemperatureId: BATCH_PACKAGING_ROOM_TEMPERATURE_ID.toString(),
      packagingRefrigeratedDryId: BATCH_PACKAGING_REFRIGERATED_DRY_ID.toString(),

      icons: [
        {
          id: BATCH_PACKAGING_FROZEN_ID.toString(),
          value: true,
          label: "Congelado",
          color: undefined,
          src: undefined,
        },
        {
          id: BATCH_PACKAGING_REFRIGERATED_ID.toString(),
          value: true,
          label: "Refrigerado",
          color: "background-color-royal-blue",
          src: "/static/images/icon-refrigerated.svg",
        },
        {
          id: BATCH_PACKAGING_ROOM_TEMPERATURE_ID.toString(),
          value: true,
          label: "Ambiente",
          color: "background-color-jaffa",
          src: "/static/images/icon-room-temperature.svg",
        },
        {
          id: BATCH_PACKAGING_REFRIGERATED_DRY_ID.toString(),
          value: true,
          label: "Refrigerado Seco",
          color: "background-color-shakespeare",
          src: "/static/images/icon-refrigerated-dry.svg",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["collectionPointLabels", "toggle", "webSocketStatus", "batchesCurrentTab"]),

    collectionPointOptions() {
      if (!this.collectionPointLabels || this.collectionPointLabels.length === 0) {
        return [];
      }

      const allOption = {
        label: "Todos",
        value: "Todos",
        id: "ALL",
      };

      return [allOption, ...this.collectionPointLabels];
    },

    isAllowedCollectionPoint() {
      const collectPlace = this.toggle?.collectPlace;
      return collectPlace && this.collectionPointLabels?.length > 0;
    },
    startDate() {
      return formatDate(this.dateSelected?.start, 4);
    },
    endDate() {
      return formatDate(this.dateSelected?.end, 4);
    },

    limitDate() {
      return moment(this.today, "DD/MM/YYYY").subtract(this.limitDaysBatches, "days").toDate();
    },

    dateSelectOptions() {
      return [
        {
          itemValue: "TD",
          value: "transferDate",
          label: "Período",
          placeholder: "Período",
          limitDays: this.limitDaysBatches,
          limitDate: this.limitDate,
          type: "date",
        },
      ];
    },

    batchFilter() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        packagingType: this.packagingSelected.slice(),
        integrated: this.integratedRoot,
        collectionPointName: this.collectionPoint?.value === "Todos" ? undefined : collectionPointName(this.collectionPoint),
      };
    },
  },

  watch: {
    '$route.query': {
      async handler(query) {
        if (!this.filtersInitialized || !/^\/print-tag\/batches\/?$/.test(this.$route.path) ||
          arePrintFiltersEqual(query, this.lastAppliedFilters || {})) return
        this.filtersInitialized = false
        this.setInitialFilters()
        this.getSelectedCollectionPoint()
        await this.$nextTick()
        if (this._isDestroyed) return
        this.filtersInitialized = true
        this.submitSearch()
      },
    },
    dateSelected(newValue) {
      if (newValue) {
        this.dateQuery = `${moment(newValue.start).format("DD/MM/YYYY")} - ${moment(newValue.end).format(
          "DD/MM/YYYY"
        )}`;
      }
    },
  },

  created() {
    this.setInitialFilters();
  },

  async mounted() {
    await this.$nextTick();
    if (this._isDestroyed) return;
    this.filtersInitialized = true;
    this.submitSearch();
    await this.getAllCollectionPointList();
    await this.$nextTick();
    if (!this._isDestroyed) this.getSelectedCollectionPoint();
  },

  beforeDestroy() {
    this.filtersInitialized = false;
  },

  methods: {
    ...mapActions(["selectBatchList", "batchGetBatches", "getAllCollectionPointList"]),

    setInitialFilters() {
      this.collectionPoint = null;
      this.typeDateSelected = "TD";
      this.dateSelected = { start: this.today, end: this.today };
      this.packagingSelected = this.icons.map(icon => icon.id);
      this.setFiltersFromUrl();
      if (!this.hasRestoredCollectionPoint && this.toggle?.collectPlace) {
        const savedCollectionPoint = Cookies.get("selectedCollectionPoint");
        if (savedCollectionPoint) {
          try {
            const point = JSON.parse(savedCollectionPoint);
            const name = collectionPointName(point);
            if (!name) throw new Error('Invalid collection point selection');
            this.collectionPoint = { ...point, label: name };
          } catch (error) {
            Cookies.remove("selectedCollectionPoint");
            logCollectionPointError('restore-selection', error);
          }
        }
      }
    },

    setFiltersFromUrl() {
      const query = restorePrintFilters('batches', this.$route.query);

      if (query.startDate && query.endDate) {
        this.dateSelected = {
          start: this.parseDate(query.startDate),
          end: this.parseDate(query.endDate),
        };
      }

      this.typeDateSelected = query.dateType || "TD";
      if (query.packagingType !== undefined) {
        this.packagingSelected = Array.isArray(query.packagingType)
          ? query.packagingType.map(String)
          : String(query.packagingType || "").split(",").filter(Boolean);
      }
      this.icons.forEach(icon => {
        icon.value = this.packagingSelected.includes(icon.id);
      });
      this.integratedRoot = query.integrated === true || query.integrated === 'true'
        ? true : query.integrated === false || query.integrated === 'false' ? false : '';
      this.hasRestoredCollectionPoint = query.collectionPointName !== undefined;
      if (query.collectionPointName) {
        this.collectionPoint = {
          label: collectionPointName({ label: query.collectionPointName }),
          value: collectionPointName({ label: query.collectionPointName }),
          id: query.collectionPointId,
        };
      }
    },

    getSelectedCollectionPoint() {
      if (!this.isAllowedCollectionPoint) return;

      this.showModalCollectionPointModal = !this.hasRestoredCollectionPoint && !this.collectionPoint;
      if (this.collectionPoint) {
        this.collectionPoint = this.collectionPointOptions.find(option =>
          collectionPointName(option) === collectionPointName(this.collectionPoint)
        ) || this.collectionPoint;
        this.collectionPoint = { ...this.collectionPoint, label: collectionPointName(this.collectionPoint) };
      }
      if (this.$refs.refSearchCollectionPoint) {
        this.$refs.refSearchCollectionPoint.field = this.collectionPoint;
      }
    },

    setDate(e) {
      if (!this.filtersInitialized || !e?.date?.start || !e?.date?.end) return;
      const date = { start: e.date.start, end: e.date.end };
      const unchanged = moment(date.start).isSame(this.dateSelected?.start, "day") &&
        moment(date.end).isSame(this.dateSelected?.end, "day");
      if (unchanged) return;

      this.dateSelected = date;
      this.submitSearch();
    },

    setPackagingType(icon, value) {
      const { id } = icon;
      const allSelected = this.icons.every((item) => item.value);

      if (allSelected) {
        this.deselectAllExcept(id);
      } else {
        this.toggleSelection(icon, value);
      }

      this.submitSearch();
    },

    deselectAllExcept(id) {
      this.icons.forEach((item) => {
        item.value = item.id === id;
      });
      this.packagingSelected = [id];
    },

    toggleSelection(icon, value) {
      icon.value = value;
      const index = this.packagingSelected.indexOf(icon.id);

      if (index === -1) {
        this.packagingSelected.push(icon.id);
      } else {
        this.packagingSelected.splice(index, 1);
      }
    },

    setCollectionPoint(event) {
      this.hasRestoredCollectionPoint = true;
      // vue-select also emits change when we synchronize its selected value.
      if (event === this.collectionPoint) return;
      const name = collectionPointName(event);
      this.collectionPoint = name ? { ...event, label: name } : null;
      try {
        if (this.collectionPoint) {
          Cookies.set("selectedCollectionPoint", JSON.stringify(this.collectionPoint), { expires: 7 });
        } else {
          Cookies.remove("selectedCollectionPoint");
        }
      } catch (error) {
        logCollectionPointError('save-selection', error);
      }
      if (this.$refs.refSearchCollectionPoint) {
        this.$refs.refSearchCollectionPoint.field = this.collectionPoint;
      }
      if (this.filtersInitialized) this.submitSearch();
    },

    setCollectionPointModal(event) {
      this.showModalCollectionPointModal = false;
      this.setCollectionPoint(event);
    },

    submitSearch() {
      if (!this.filtersInitialized) return Promise.resolve();
      if (!this.pendingSearch) {
        this.pendingSearch = new Promise(resolve => setTimeout(resolve, 0)).then(() => {
          this.pendingSearch = null;
          return this.executeSearch();
        });
      }
      return this.pendingSearch;
    },

    async executeSearch() {
      if (!this.filtersInitialized) return;
      this.lastAppliedFilters = this.getCurrentFilters();
      await persistPrintFilters(this, 'batches', this.lastAppliedFilters);
      if (!this.filtersInitialized) return;
      const validation = this.validateDateRange(this.dateSelected?.start, this.dateSelected?.end);
      if (!validation.isValid) {
        notify({ title: "AVISO:", message: validation.errorMessage });
        return;
      }
      try {
        this.selectBatchList(this.batchFilter);
        await this.batchGetBatches();
      } catch (error) {
        this.handleBackendError(error);
      }
    },

    hintPackagingType(value) {
      const packagingSelected = this.packagingSelected;
      const hintSelect = "Selecione para filtrar uma ou mais formas de acondicionamento";
      const hintDeselect = "Clique para remover filtro";

      if (packagingSelected?.length === 4 && value) return hintSelect;

      if (value) return hintDeselect;

      return hintSelect;
    },

    parseDate(dateStr) {
      return moment(dateStr, "DD/MM/YYYY", true).toDate();
    },

    validateDateRange(startDate, endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      const maxDays = this.limitDaysBatches;
      const diffDays = end.diff(start, "days");
      const daysFromToday = moment(this.today).startOf('day').diff(start.clone().startOf('day'), "days");
      const validDates = !!startDate && !!endDate && start.isValid() && end.isValid() && diffDays >= 0;
      const isValidRange = validDates && diffDays <= maxDays;
      const isValidFromToday = validDates && daysFromToday <= maxDays;
      const isValid = isValidRange && isValidFromToday;

      let errorMessage = "";
      if (!validDates) {
        errorMessage = "Selecione um período válido, com a data inicial anterior ou igual à final.";
      } else if (!isValidRange && !isValidFromToday) {
        errorMessage = `Selecione um período de até ${maxDays} dias e não anterior a ${maxDays} dias atrás.`;
      } else if (!isValidRange) {
        errorMessage = `Selecione um período de até ${maxDays} dias para lotes.`;
      } else if (!isValidFromToday) {
        errorMessage = `Não é possível buscar lotes anteriores a ${maxDays} dias atrás.`;
      }
      return { isValid, errorMessage };
    },

    handleBackendError(error) {
      logCollectionPointError('load-batches', error);
      notify({ title: "AVISO:", message: "Erro ao buscar lotes. Tente novamente." });
    },

    getCurrentFilters() {
      return {
        tab: this.batchesCurrentTab,
        startDate: this.startDate,
        endDate: this.endDate,
        dateType: this.typeDateSelected,
        packagingType: this.packagingSelected.join(","),
        integrated: this.integratedRoot,
        collectionPointName: collectionPointName(this.collectionPoint) || '',
        collectionPointId: this.collectionPoint?.id || '',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/components/batch-filter-controls';

:deep(.card-form__container) {
  padding-top: 8px !important;
}

.w-100 {
  width: 100%;
}

</style>
